import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Spinner from 'Components/Atoms/Spinner';
import { useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';
import OutlineUpload from '../OutlineIcons/OutlineUpload';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export default function InputFile(props) {
    const {
        onUpload,
        className,
        multiple,
        isBusy,
        maxFileSize,
        acceptedFileTypes,
        errorMessages: errorMessagesProp,
        label,
    } = props;

    const [dragging, setDragging] = useState(false);
    const [error, setError] = useState(null);
    const timer = useRef(null);

    const { t } = useTranslation('common');

    const MAX_NAME_LENGTH = 128;

    const errorMessages = {
        fileTooLarge: errorMessagesProp?.fileTooLarge || t('file-too-large'),
        invalidFileType: errorMessagesProp?.invalidFileType || t('invalid-file-type'),
        fileNameTooLong: errorMessagesProp?.fileNameTooLong || t('file-name-too-long'),
    };

    useEffect(() => {
        if (error) {
            setTimeout(() => {
                setError(null);
            }, 6000);
        }
    }, [error]);

    const debounceDragLeave = useCallback(() => {
        timer.current = setTimeout(() => {
            setDragging(false);
        }, 100);
    }, []);

    const hasInvalidFormats = (files) => {
        if (!acceptedFileTypes) return false;

        const acceptedFormats = acceptedFileTypes.split(',').map((format) => format.trim());

        return Array.from(files).some((file) => !acceptedFormats.includes(file.type));
    };

    const dragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        clearTimeout(timer.current);
        setDragging(true);
    }, []);

    const hasInvalidSizes = (files) => {
        if (!maxFileSize) return false;

        return Array.from(files).some((file) => file.size > maxFileSize);
    };

    const hasInvalidNames = (files) => {
        return Array.from(files).some((file) => file.name.length > MAX_NAME_LENGTH);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            if (hasInvalidSizes(e.dataTransfer.files)) {
                setError(errorMessages.fileTooLarge);
                return;
            }

            if (hasInvalidFormats(e.dataTransfer.files)) {
                setError(errorMessages.invalidFileType);
                return;
            }

            if (hasInvalidNames(e.dataTransfer.files)) {
                setError(errorMessages.fileNameTooLong);
                return;
            }

            if (multiple) {
                onUpload(e.dataTransfer.files);
            } else {
                onUpload(e.dataTransfer.files[0]);
            }
        }
    };

    const handleFileChange = (e) => {
        if (hasInvalidSizes(e.target.files)) {
            setError(errorMessages.fileTooLarge);
            return;
        }

        if (hasInvalidFormats(e.target.files)) {
            setError(errorMessages.invalidFileType);
            return;
        }

        if (hasInvalidNames(e.target.files)) {
            setError(errorMessages.fileNameTooLong);
            return;
        }

        if (multiple) {
            onUpload(e.target.files);
        } else {
            onUpload(e.target.files[0]);
        }
    };

    return (
        <div>
            <div
                className={cn(
                    'relative h-20 flex rounded-md border-dashed border border-gray-300 bg-gray-50 text-xs text-gray-500',
                    dragging && 'border-primary text-primary bg-[#f8f7fc]',
                    className,
                )}
                onDragEnter={dragEnter}
                onDragLeave={debounceDragLeave}
                onDragOver={dragEnter}
                onDrop={handleDrop}
            >
                {isBusy ? (
                    <div className="w-full h-full justify-center flex items-center">
                        <Spinner size={24} />
                    </div>
                ) : (
                    <label className="cursor-pointer w-full h-full justify-center flex flex-col items-center gap-1">
                        <OutlineUpload color="currentColor" />
                        {label && <div>{label}</div>}
                        <input
                            accept={acceptedFileTypes}
                            type="file"
                            name="files[]"
                            className="hidden"
                            multiple={multiple}
                            onChange={handleFileChange}
                        />
                    </label>
                )}
            </div>
            <ErrorMessage message={error} />
        </div>
    );
}

InputFile.propTypes = {
    onUpload: PropTypes.func.isRequired,
    isBusy: PropTypes.bool,
    className: PropTypes.string,
    multiple: PropTypes.bool,
    maxFileSize: PropTypes.number,
    overlayText: PropTypes.string,
    acceptedFileTypes: PropTypes.string,
    label: PropTypes.string,
    errorMessages: PropTypes.object,
};
